import React from 'react';

import { Grid, makeStyles, Typography } from '@material-ui/core';
import i18n from 'i18n';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import palette from 'theme/palette';
import ApplicationState from 'store/interfaces';
import { TableCellHeaderProps } from 'components/TableItems/interfaces';
import TableItems, { TableCell, TableRow } from 'components/TableItems';
import { formatCurrency, formatPercent } from 'utils/helpers';
import {
  ListFlavorsCost,
  PeriodCountAccess,
  FlavorsCostFields,
  DateFilterFields,
} from 'modules/environments/interfaces';
import { getFlavorsCostRequest } from 'modules/environments/actions';
import { useTableFilters } from 'hooks/useTableFilters';
import styles from './styles';

const useStyles = makeStyles(styles);

const initialQuery = {
  offset: 0,
  limit: 10,
  search: '',
  period: '',
};

interface FlavorsCostProps {
  flavorsCostList: ListFlavorsCost;
  intervalTime: PeriodCountAccess;
  isLoading: boolean;
  dateFilter?: DateFilterFields;
  getFlavorsCostRequest: (props) => void;
}

const FlavorsCost = ({
  flavorsCostList,
  intervalTime,
  isLoading,
  dateFilter,
  getFlavorsCostRequest,
}: FlavorsCostProps) => {
  const classes = useStyles({});
  const { id } = useParams<{
    id: string;
  }>();
  const { query, itemOrder, handleOrder, handlePageChange } = useTableFilters(
    initialQuery
  );

  const headers: Array<TableCellHeaderProps> = [
    {
      key: 'flavor',
      i18n: 'Flavor',
      order: true,
      style: { width: '30%' },
    },
    {
      key: 'num_instances',
      i18n: i18n.t('INSTANCES_KEY'),
      order: true,
    },
    {
      key: 'total_cost',
      i18n: i18n.t('COSTS_PAGE.COST_KEY'),
      order: true,
    },
    {
      key: 'cost_percentage',
      i18n: i18n.t('COSTS_PAGE.COST_PERCENTAGE_KEY'),
      order: true,
    },
  ];

  React.useEffect(() => {
    const data =
      dateFilter?.['start-date'] && dateFilter?.['end-date']
        ? dateFilter
        : { period: intervalTime };

    getFlavorsCostRequest({
      query: {
        ...query,
        ...data,
      },
      id,
    });
  }, [getFlavorsCostRequest, query, intervalTime, id, dateFilter]);

  return (
    <Grid item className={classes.flavorsCostContainer}>
      <Grid container alignItems="center" justify="space-between">
        <Typography className={classes.graphTitle}>
          {i18n.t('COSTS_PAGE.FLAVORS_COST_TITLE_KEY')}
        </Typography>
      </Grid>

      <Grid container className={classes.flavorsCostTable}>
        <TableItems
          header={
            <>
              {headers.map(item => (
                <TableCell
                  key={item?.key}
                  centerTitle={item?.centerTitle}
                  textAlignCenter={item?.textAlignCenter}
                  order={item?.order}
                  markOderItem={itemOrder === item?.key}
                  handleOrder={() =>
                    item?.order ? handleOrder(item?.key) : null
                  }
                  style={{
                    ...item?.style,
                    backgroundColor: palette.deepBlue,
                    color: palette.white,
                  }}
                >
                  {item?.i18n}
                </TableCell>
              ))}
            </>
          }
          renderItems={(item: FlavorsCostFields) => (
            <TableRow key={item?.flavor}>
              <TableCell>{item?.flavor}</TableCell>
              <TableCell>{item?.num_instances.toLocaleString()}</TableCell>
              <TableCell>
                {item?.total_cost && formatCurrency(item?.total_cost)}
              </TableCell>
              <TableCell>
                {item?.cost_percentage && formatPercent(item?.cost_percentage)}
              </TableCell>
            </TableRow>
          )}
          data={flavorsCostList}
          onChangePage={handlePageChange}
          onChangeLimit={() => null}
          isLoading={isLoading}
          notFoundText={i18n.t('COSTS_PAGE.NO_RESULTS')}
          notRowsPerPage
          overrideRowsCount={10}
          isFixed
          fillTable={flavorsCostList?.results?.length === 1}
        />
      </Grid>
    </Grid>
  );
};

const mapStateToProps = ({
  environments: { flavorsCostList, periodFilter, flavorsCostIsLoading },
}: ApplicationState) => ({
  flavorsCostList,
  intervalTime: periodFilter,
  isLoading: flavorsCostIsLoading,
});

export default connect(mapStateToProps, {
  getFlavorsCostRequest,
})(FlavorsCost);
